import { useState } from 'react';
import Loader from '../../common/Loader';
import { Box, Button, Chip, Typography } from '@mui/material';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { FormProvider, RHFSelectField, RHFTextField } from '../../common/hook-form';
import RHFDateRangePickerField2 from '../../common/hook-form/RHFDatePicker2';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { apiConstants, postGeneric } from '../../common/constants';
import _ from 'lodash';
import { formatDate } from '../../helpers/UtilityHelper';
import MUIModal from '../../common/MUIModal';
import { getMerchant } from '../../helpers/ReduxHelpers';
import * as XLSX from 'xlsx';
import { Download, PlayArrow } from '@mui/icons-material';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { CallKpiCards, CallKpiSkeleton, CallOutcomeTabs } from './CallStatsUI';
import { primaryTextSx, secondaryTextSx, cellStackSx, awbTextSx } from '../orders/list2/cellStyles';
import { FONT, SIZE } from '../../styles/foundation/typography';
import { filterBarSx, primaryBtnSx, secondaryBtnSx } from '../../styles/patterns';
import { SUCCESS, ERROR, SUBTLE, PRIMARY_BLUE, WARNING } from '../../styles/foundation/colors';
import AwbLinkCell from '../../common/AwbLinkCell';

// Feedback item 4a: /ndr/recording/search returns call_duration as decimal MINUTES (fractional part = seconds/60),
// same convention as the admin panel's formatCallDuration (pages/ndr/NdrCalls.js) — kept consistent with dev's fix here.
const formatCallDuration = (duration) => {
    const v = Number(duration);
    if (v == null || isNaN(v) || v <= 0) return '-';
    const m = Math.floor(v);
    const s = Math.round((v - m) * 60);
    if (m > 0 && s > 0) return `${m} min ${s} sec`;
    if (m > 0) return `${m} min`;
    return `${s} sec`;
};

const NDRCall = () => {
    const [page, setPage] = useState(0);
    const [filterChange, setFiltersChange] = useState(0);
    const [filters, setFilters] = useState();
    const [data, setData] = useState();
    const [sizePerPage, setSizePerPage] = useState(20);
    const [loading, setLoading] = useState(false);
    const [ndrQ, setNdrQ] = useState([])
    const [ndrQModal, setNdrQModal] = useState(false)
    const [analytics, setAnalytics] = useState(null)
    const [analyticsLoading, setAnalyticsLoading] = useState(true)
    // Connected / Not Connected bifurcation — resolved server-side on ndr_result.
    const [callTab, setCallTab] = useState('CONNECTED')
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''

    const CALL_TABS = [
        { key: 'CONNECTED', label: 'Connected', icon: <PhoneInTalkIcon />, color: SUCCESS, help: 'Customer answered the call' },
        { key: 'NOT_CONNECTED', label: 'Not Connected', icon: <PhoneMissedIcon />, color: ERROR, help: 'Call did not connect' },
    ]

    const methods = useForm({
        defaultValues: {
            awbnumber: '',
            ndrResults: '',
            callFromDate: '',
            callToDate: ''
        }
    });
    const {
        handleSubmit,
        reset,
    } = methods;

    const NDRResults = ["Reattempt Delivery", "Cancel Delivery", "Delivered", "Wrong Number", "Not Connected",
        "Reattempt Delivery Address Updated", "Busy or Improper Response", "Language Barrier", "Redial"]
    const options = NDRResults.map((op) => ({ label: _.startCase(op), value: op }))

    const handleFilterSubmit = (param) => {
        setFiltersChange(filterChange + 1)
        setFilters(param)
    }

    useEffect(() => {
        setPage(0)
    }, [sizePerPage, filters, callTab])

    useEffect(() => {
        setLoading(true)
        const filtersToApply = [{ field: "merchantOid", operator: "eq", value: merchantId }];
        if (filters?.awbnumber) {
            filtersToApply.push({ field: "awb_number", operator: "eq", value: filters.awbnumber });
        }
        if (filters?.ndrResults) {
            filtersToApply.push({ field: "ndr_result", operator: "eq", value: filters.ndrResults });
        }
        // Date range on call time
        if (filters?.callFromDate) {
            filtersToApply.push({ field: "call_start_time", operator: "gte", value: formatDate(filters.callFromDate, 'yyyy-MM-dd') + 'T00:00:00' });
        }
        if (filters?.callToDate) {
            filtersToApply.push({ field: "call_start_time", operator: "lte", value: formatDate(filters.callToDate, 'yyyy-MM-dd') + 'T23:59:59' });
        }
        // Connected / Not Connected tab (bifurcation on ndr_result)
        if (callTab === 'CONNECTED') {
            filtersToApply.push({ field: "ndr_result", operator: "ne", value: "Not Connected" });
        } else {
            filtersToApply.push({ field: "ndr_result", operator: "eq", value: "Not Connected" });
        }
        const payload = {
            // "collection": "futworks_ndr_recording",
            "offset": page * sizePerPage,
            "limit": sizePerPage,
            "skipCount": true,
            "countOnly": false,
            "sort": {
                "direction": "DESC",
                "field": "created_at"
            }, "andfilter": filtersToApply
        }
        postGeneric(apiConstants.NDR_RECORDING_SEARCH, payload).then((res) => {
            setLoading(false)
            setData(res?.data?.data)
        }).catch(() => {
            setLoading(false)
        })
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        const formatDate2 = (d) => d.toISOString().slice(0, 10);
        setAnalyticsLoading(true)
        postGeneric('intervention/calls/analytics', {
            type: "NDR",
            merchantOids: [merchantId],
            dateRange: { from: formatDate2(thirtyDaysAgo), to: formatDate2(today) },
        }).then((res) => {
            setAnalytics(res?.data);
        }).catch(() => {
            setAnalytics(null);
        }).finally(() => setAnalyticsLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizePerPage, page, filterChange, filters, callTab])

    const handleDownloadExcel = () => {
        if (!data || data.length === 0) return;

        const exportData = data.map(row => ({
            'AWB No.': row.awb_number,
            'NDR Results': row.ndr_result,
            'Call Time': formatDate(row.call_start_time),
            'Call Duration (Mins)': formatCallDuration(row.call_duration),
            'Outcome': row.ndr_outcome ? 'TRUE' : 'FALSE',
            'Recording URL': row.recording_url || '-',
            'Call Response': row.call_responses?.length > 0 ? 'Available' : '-',
            'Questions and Answers': row.call_responses?.map(response =>
                `Question: ${response.question}\nAnswer: ${response.answer}`
            ).join('\n\n') || '-'
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        // Set column widths
        const colWidths = [
            { wch: 15 }, // AWB No.
            { wch: 25 }, // NDR Results
            { wch: 20 }, // Call Time
            { wch: 15 }, // Call Duration
            { wch: 10 }, // Outcome
            { wch: 30 }, // Recording URL
            { wch: 15 }, // Call Response
            { wch: 50 }, // Questions and Answers
        ];
        ws['!cols'] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'NDR Calls');
        XLSX.writeFile(wb, 'ndr_calls.xlsx');
    };

    const columns = [
        {
            field: 'awb_number', headerAlign: "left", hideSortIcons: true, headerName: 'AWB No.', flex: 0.8, minWidth: 140, renderCell: (params) => {
                const { row } = params;
                return (
                    <AwbLinkCell awb={row.awb_number} sx={awbTextSx} />
                )
            },
        },
        {
            field: 'channel', headerAlign: "left", hideSortIcons: true, headerName: 'Calling Mode', flex: 1, minWidth: 100, renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography sx={secondaryTextSx}>
                        {'Manual'}
                    </Typography>
                )
            },
        },
        {
            field: 'ndr_outcome', headerAlign: "left", hideSortIcons: true, headerName: 'Outcome', flex: 1, minWidth: 130, renderCell: (params) => {
                const { row } = params;
                const isNotConnected = row.ndr_result === 'Not Connected';
                const isConnected = row.ndr_result && !isNotConnected;
                return (
                    <Chip size="small" label={isNotConnected ? 'Not Connected' : 'Connected'} sx={{
                        fontFamily: FONT,
                        fontSize: 11,
                        fontWeight: 600,
                        height: 24,
                        bgcolor: isConnected ? SUCCESS + '18' : ERROR + '18',
                        color: isConnected ? SUCCESS : ERROR,
                        border: '1px solid ' + (isConnected ? SUCCESS + '40' : ERROR + '40'),
                        '& .MuiChip-label': { px: 1 }
                    }} />
                )
            },
        },
        {
            field: 'ndr_result', headerAlign: "left", hideSortIcons: true, headerName: 'NDR Result', flex: 1, minWidth: 160, renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography sx={secondaryTextSx}>
                        {row.ndr_result || '-'}
                    </Typography>
                )
            },
        },
        {
            field: 'call_start_time', headerAlign: "left", hideSortIcons: true, headerName: 'Call Time', flex: 1.1, minWidth: 100, renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography sx={secondaryTextSx}>
                        {formatDate(row.call_start_time, "dd MMM, HH:mm")}
                    </Typography>
                )
            },
        },
        {
            field: 'call_duration', headerAlign: "left", hideSortIcons: true, headerName: 'Call Duration', flex: 1, minWidth: 120, renderCell: (params) => {
                const { row } = params;
                return (
                    <Typography sx={secondaryTextSx}>
                        {formatCallDuration(row?.call_duration)}
                    </Typography>
                )
            },
        },
        
        {
            field: 'recording_url', headerAlign: "left", hideSortIcons: true, headerName: 'Recordings', flex: 1, minWidth: 120,
            renderCell: (params) => {
                const { row } = params;
                if (!row.recording_url) return <Typography sx={secondaryTextSx}>-</Typography>;
                return (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlayArrow />}
                        href={row.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', px: 1.5, py: 0.3, minWidth: 0 }}
                    >
                        Play
                    </Button>
                )
            },
        },
        {
            field: 'call_responses', align: 'center', headerAlign: "center", hideSortIcons: true, headerName: 'Call Response', flex: 1, minWidth: 140, renderCell: (params) => {
                const { row } = params;
                if (row?.call_responses && row.call_responses.length > 0) {
                    return (
                        <Button
                            sx={{
                                fontFamily: FONT,
                                fontSize: SIZE['2xs'],
                                textTransform: 'none',
                                p: 0,
                                minWidth: 0
                            }}
                            onClick={() => {
                                setNdrQ(row.call_responses);
                                setNdrQModal(true);
                            }}
                        >
                            View Q&A
                        </Button>
                    )
                }
                else {
                    return ("-")
                }
            },
        }
    ];


    // KPI computations
    const totalCalls = analytics?.totalCalls || 0;
    const connected = analytics?.connected || 0;
    const connectRate = analytics?.connectRate || 0;
    const avgDurationConnected = connected > 0
        ? Math.round((analytics?.avgDuration_seconds || 0) * totalCalls / connected)
        : 0;
    const avgDurMin = Math.floor(avgDurationConnected / 60);
    const avgDurSec = avgDurationConnected % 60;
    const avgDurDisplay = avgDurMin > 0 ? `${avgDurMin}m ${avgDurSec}s` : `${avgDurSec}s`;

    const outcomes = analytics?.outcomeDistribution || {};
    const reattemptCount = (outcomes['Reattempt Delivery'] || 0) + (outcomes['Reattempt Delivery Address Updated'] || 0) + (outcomes['Future Delivery Required'] || 0);
    const rtoCount = (outcomes['Cancel Delivery'] || 0) + (outcomes['RTO New'] || 0);
    const deliveredCount = outcomes['Delivered'] || 0;
    const notConnectedCount = outcomes['Not Connected'] || 0;
    const busyCount = (outcomes['Busy or Improper Response'] || 0) + (outcomes['Voicemail'] || 0);
    const otherCount = (outcomes['Language Barrier'] || 0) + (outcomes['Wrong Number'] || 0) + (outcomes['Redial'] || 0) + (outcomes['Escalations'] || 0) + (outcomes['Unprocessed'] || 0);

    const summaryCards = [
        { label: 'Total Calls', value: totalCalls.toLocaleString(), color: PRIMARY_BLUE, icon: <CallOutlinedIcon /> },
        { label: 'Connected', value: connected.toLocaleString(), sub: `${connectRate}% connect rate`, color: SUCCESS, icon: <PhoneInTalkIcon /> },
        { label: 'Avg Duration (Connected)', value: avgDurDisplay, color: PRIMARY_BLUE, icon: <AccessTimeIcon /> },
    ];

    const dispositionCards = [
        { label: 'Reattempt', value: reattemptCount.toLocaleString(), color: SUCCESS },
        { label: 'RTO / Cancel', value: rtoCount.toLocaleString(), color: ERROR },
        { label: 'Delivered', value: deliveredCount.toLocaleString(), color: SUCCESS },
        { label: 'Not Connected', value: notConnectedCount.toLocaleString(), color: ERROR },
        { label: 'Busy / Voicemail', value: busyCount.toLocaleString(), color: WARNING },
        { label: 'Other', value: otherCount.toLocaleString(), color: SUBTLE },
    ];

    // Connected / Not Connected are complementary; derive the not-connected count for the tab badges.
    const notConnectedTotal = Math.max(totalCalls - connected, 0);
    const callTabs = CALL_TABS.map((t) => ({
        ...t,
        count: (t.key === 'CONNECTED' ? connected : notConnectedTotal).toLocaleString(),
    }));

    return (
        <>
            <Box>
                {/* KPI skeletons — shown on first load so the page doesn't "pop" when analytics resolves */}
                {!analytics && analyticsLoading && <CallKpiSkeleton />}
                {/* KPI Cards */}
                {analytics && <CallKpiCards summaryCards={summaryCards} dispositionCards={dispositionCards} />}

                {/* Connected / Not Connected tabs — self-describing so any user knows what they're seeing */}
                <CallOutcomeTabs tabs={callTabs} value={callTab} onChange={setCallTab} />

                <Box sx={filterBarSx}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(handleFilterSubmit)}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <Box sx={{ flex: '1 1 160px', minWidth: 150 }}>
                                <RHFTextField name="awbnumber" label="AWB Number" size="small" />
                            </Box>
                            <Box sx={{ flex: '1 1 160px', minWidth: 150 }}>
                                <RHFSelectField name="ndrResults" label="NDR Results" size="small" width='100%' options={_.orderBy(options, 'value')} />
                            </Box>
                            <Box sx={{ flex: '1 1 200px', minWidth: 180 }}>
                                <RHFDateRangePickerField2 label="Call Date" maxDate={new Date()} fromDateName="callFromDate" toDateName="callToDate" />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                <Button type="submit" sx={primaryBtnSx}>Filter</Button>
                                <Button sx={secondaryBtnSx} onClick={() => {
                                    reset({
                                        awbnumber: '',
                                        ndrResults: '',
                                        callFromDate: '',
                                        callToDate: '',
                                    })
                                    setFilters()
                                    setFiltersChange((c) => c + 1)
                                }}>Reset</Button>
                                <Button
                                    sx={secondaryBtnSx}
                                    onClick={handleDownloadExcel}
                                    endIcon={<Download />}
                                    disabled={!data || data.length === 0}
                                >
                                    Export NDR Call Recordings
                                </Button>
                            </Box>
                        </Box>
                    </FormProvider>
                </Box>
                {loading && <Loader />}
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={data ? data.map((row, index) => ({ ...row, id: index + 1, })) : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    rowCount={Number.MAX_VALUE}
                    hideCountNumber={true}
                    filterMode={"server"}
                    getRowId={(row) => row.id} />
                <MUIModal
                    open={ndrQModal}
                    title="Call Details"
                    maxWidth={"sm"}
                    handleClose={() => setNdrQModal(false)}
                >
                    <ProzoDataGrid
                        columns={[{
                            field: "question", headerName: "Question", flex: 3, minWidth: 300, hideSortIcons: true
                        }, {
                            field: "answer", flex: 1, headerName: "Answer", minWidth: 150, hideSortIcons: true
                        },]}
                        autoHeight={true}
                        rows={ndrQ || []}
                        pagination={false}
                        hideFooterPagination={true}
                        getRowId={(row) => Math.random()}
                    />
                </MUIModal>
            </Box>
        </>
    );
}
export default NDRCall;
